<template>
  <div
    class="vcb"
    :class="['vcb--' + orientation, 'vcb--' + variant]"
    @click="$emit('click', $event)"
  >
    <div class="vcb__inner">
      <smart-img
        v-if="cover"
        class="vcb__img"
        mode="content"
        :size="imageSize"
        :src="cover"
        alt="视频封面"
        :eager="eager"
      />
      <span class="vcb__play">
        <svg viewBox="0 0 24 24"><path d="M9 6.8v10.4a.8.8 0 0 0 1.22.68l8.1-5.2a.8.8 0 0 0 0-1.36l-8.1-5.2A.8.8 0 0 0 9 6.8z" fill="currentColor" /></svg>
      </span>
      <span v-if="durationText" class="vcb__duration">{{ durationText }}</span>
    </div>
  </div>
</template>

<script>
import { videoCoverOf, videoOrientation, formatVideoDuration } from '@/utils/articleMedia'

/**
 * 信息流 / 个人主页里视频帖的封面块：封面 + 居中播放图标 + 右下角时长（App 端 ArticleCard 的 video-block 同款）。
 * 列表里不自动播放，点了打开播放器（点击由外层卡片处理，这里只把 click 抛出去）。
 *
 * 版式按视频比例分三档：竖版 3:4、方形 1:1、横版 16:9。不知道宽高的老数据按竖版处理，手机拍的视频绝大多数是竖的。
 * 封面图按比例完整铺满（object-fit: cover），不像九宫格那样裁成正方形，所以用 content 档缩略图。
 *
 * variant：feed 桌面信息流；mobile 移动信息流（宽度按容器百分比）；compact 个人主页列表
 */
export default {
  name: 'VideoCoverBlock',
  props: {
    article: { type: Object, required: true },
    variant: { type: String, default: 'feed' },
    eager: { type: Boolean, default: false }
  },
  computed: {
    cover () {
      return videoCoverOf(this.article)
    },
    orientation () {
      return videoOrientation(this.article)
    },
    durationText () {
      return formatVideoDuration(this.article && this.article.videoDuration)
    },
    imageSize () {
      return this.orientation === 'landscape' && this.variant !== 'compact' ? 1200 : 720
    }
  }
}
</script>

<style lang="scss" scoped>
.vcb {
  position: relative;
  margin: 8px 0;
  border-radius: 8px;
  overflow: hidden;
  background: #1f1f1f;
  cursor: pointer;
}

.vcb__inner {
  position: relative;
  width: 100%;
  height: 0;
}

.vcb--portrait .vcb__inner {
  padding-top: 133.333%;
}

.vcb--square .vcb__inner {
  padding-top: 100%;
}

.vcb--landscape .vcb__inner {
  padding-top: 56.25%;
}

// 桌面信息流：竖版不宜太高，横版铺到和四宫格差不多宽
.vcb--feed {
  &.vcb--portrait {
    width: 240px;
  }

  &.vcb--square {
    width: 280px;
  }

  &.vcb--landscape {
    width: 100%;
    max-width: 480px;
  }

  // 窄屏下桌面卡片也会出现（平板竖屏），按容器宽度收
  @media screen and (max-width: $bp-md - 1px) {
    &.vcb--portrait {
      width: 58%;
    }

    &.vcb--square {
      width: 66%;
    }
  }
}

// 移动信息流：和单张大图（62%）同一档，横版占满
.vcb--mobile {
  margin-top: 10px;
  border-radius: 6px;

  &.vcb--portrait {
    width: 58%;
  }

  &.vcb--square {
    width: 66%;
  }

  &.vcb--landscape {
    width: 100%;
  }
}

// 个人主页列表：和 138px 的配图格子高度相近
.vcb--compact {
  &.vcb--portrait {
    width: 138px;
  }

  &.vcb--square {
    width: 184px;
  }

  &.vcb--landscape {
    width: 324px;
    max-width: 100%;
  }
}

.vcb__img {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.vcb__play {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 44px;
  height: 44px;
  margin: -22px 0 0 -22px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.38);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
  transition: transform 0.2s ease, background-color 0.2s ease;

  svg {
    width: 26px;
    height: 26px;
    margin-left: 2px;
  }
}

.vcb:hover .vcb__play {
  transform: scale(1.08);
  background: rgba(0, 0, 0, 0.5);
}

.vcb--compact .vcb__play {
  width: 36px;
  height: 36px;
  margin: -18px 0 0 -18px;

  svg {
    width: 20px;
    height: 20px;
  }
}

.vcb__duration {
  position: absolute;
  right: 6px;
  bottom: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  font-variant-numeric: tabular-nums;
}
</style>
